import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Grid, Paper, Divider, CircularProgress } from "@mui/material";


// 
// HELPER COMPONENTS FOR TABLE DISPLAY
// 

// Regular row with label and value
const Row = ({ label, value }) => (
  <tr style={{ background: "white" }}>
    <td
      style={{
        width: "30%",
        padding: "6px",
        border: "1px solid #ddd",
        fontWeight: 600,
        verticalAlign: "top",
      }}
    >
      {label}
    </td>
    <td
      style={{
        width: "70%",
        padding: "6px",
        border: "1px solid #ddd",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        verticalAlign: "top",
      }}
    >
      {value ?? "—"}
    </td>
  </tr>
);

// Section header row
const Section = ({ title }) => (
  <tr>
    <td
      colSpan={2}
      style={{
        padding: "8px",
        border: "1px solid #ddd",
        fontWeight: "bold",
        color: "#000",
      }}
    >
      {title}
    </td>
  </tr>
);

// Row with checkbox indicator
const CheckRow = ({ label, detail }) => (
  <tr style={{ background: "white" }}>
    <td
      style={{
        width: "30%",
        padding: "6px",
        border: "1px solid #ddd",
        fontWeight: 600,
        verticalAlign: "top",
      }}
    >
      <span style={{ marginRight: 8 }}>☑</span>
      {label}
    </td>
    <td
      style={{
        width: "70%",
        padding: "6px",
        border: "1px solid #ddd",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        verticalAlign: "top",
      }}
    >
      {String(detail || "").trim() ? detail : "—"}
    </td>
  </tr>
);

//
// UTILITY FUNCTIONS
//

// Format date to MM/DD/YYYY
const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// Get first non-empty value from multiple options
const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      const s = typeof v === "string" ? v.trim() : String(v);
      if (s !== "") return v;
    }
  }
  return "";
};

// Convert value to boolean
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
  return !!v;
};

// Parse JSON that might be nested (JSON string inside JSON string)
const parseJsonDeep = (input, maxDepth = 5) => {
  let v = input;
  let depth = 0;
  try {
    while (typeof v === "string" && depth < maxDepth) {
      v = JSON.parse(v);
      depth++;
    }
  } catch {
    // If parsing fails, return what we have
  }
  return v;
};

// Convert value to array
const asArray = (val) => {
  const v = parseJsonDeep(val);
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return [v];
  return [];
};

// Get value from object (handles different key formats)
const getVal = (o, name) => {
  if (!o || !name) return undefined;
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return firstNonEmpty(o[name], o[pascal]);
};

// Get any matching value from multiple key names
const getAny = (o, ...names) => firstNonEmpty(...names.map((k) => getVal(o, k)));

// 
// DATA NORMALIZER
// 

// Convert raw API data to a consistent format
const normalizeIntake = (src) => {
  if (!src) return {};

  const parsed = parseJsonDeep(src);
  const rootRaw = parsed && typeof parsed === "object" && "data" in parsed ? parsed.data : parsed;
  const root = parseJsonDeep(rootRaw) || {};

  // Helper to pick value from multiple possible key names
  const pick = (...k) => getAny(root, ...k);
  // Helper to pick boolean value
  const pickB = (...k) => toBool(getAny(root, ...k));

  return {
    DOS: pick("DOS", "dos", "assessmentDate"),
    customFormTitle: pick("customFormTitle"),
    preferredName: pick("PreferredName"),
    pronouns: pick("Pronouns"),
    cellPhone: pick("CellPhone"),
    homeAddress: pick("HomeAddress"),
    currentlyLivingSolo: pick("currentlyLivingSolo"),
    allergies: pick("listAnyAllergies", "listAnyallergies"),
    foodIntolerances: pick("listAnyFoodIntolerances"),
    dueDate: pick("DueDate", "dueDate"),
    sexOfBaby: pick("SexOfBaby", "Sex0fBaby"),
    pregnancyNumber: pick("pregnancyNumber"),
    manyChildren: pick("manyChildren"),
    namesAndAges: pick("namesAndAges", "namesAndages"),
    pregnancyAfterALoss: pickB("pregnancyAfterALoss", "pregnancyAfterAloss"),
    pregnancyAfterALossDetails: pick("pregnancyAfterALossDetails"),
    aspectsOfYourConception: pickB("aspectsOfYourConception", "aspectsOfyourConception"),
    aspectsOfYourConceptionDetails: pick("aspectsOfYourConceptionDetails"),
    previousBirth: pickB("previousBirth"),
    previousBirthDetails: pick("previousBirthDetails"),
    childbirthEducation: pickB("childbirthEducation"),
    childbirthEducationDetails: pick("childbirthEducationDetails"),
    physicalActivity: pickB("physicalActivity"),
    physicalActivityDetails: pick("physicalActivityDetails"),
    anyPractitioners: pickB("anyPractitioners"),
    anyPractitionersDetails: pick("anyPractitionersDetails"),
    normallyManagePain: pickB("normallyManagePain"),
    normallyManagePainDetails: pick("normallyManagePainDetails"),
    PastMedicalHistory: pickB("PastMedicalHistory", "pastMedicalHistory"),
    PastMedicalHistoryDetails: pick("PastMedicalHistoryDetails"),
    AnyPhysicalLimitations: pickB("AnyPhysicalLimitations", "anyPhysicalLimitations"),
    AnyPhysicalLimitationsDetails: pick("AnyPhysicalLimitationsDetails"),
    pregnancyFromFertilityTreatment: pickB("pregnancyFromFertilityTreatment"),
    pregnancyFromFertilityTreatmentDetails: pick("pregnancyFromFertilityTreatmentDetails"),
    CurrentMedicalHistory: pickB("CurrentMedicalHistory", "currentMedicalHistory"),
    CurrentMedicalHistoryDetails: pick("CurrentMedicalHistoryDetails"),
    havingIssuesCurrently: pickB("havingIssuesCurrently"),
    havingIssuesCurrentlyDetails: pick("havingIssuesCurrentlyDetails"),
    exerciseBall: pickB("exerciseBall"),
    exerciseBallDetails: pick("exerciseBallDetails"),
    historyOfTrauma: pickB("historyOfTrauma", "historyofTrauma"),
    historyOfTraumaDetails: pick("historyOfTraumaDetails"),
    petsInYourHome: pickB("petsInYourHome"),
    petsInYourHomeDetails: pick("petsInYourHomeDetails"),
    CulturalOrReligiousPractice: pickB("CulturalOrReligiousPractice", "culturalOrReligiousPractice"),
    CulturalOrReligiousPracticeDetails: pick("CulturalOrReligiousPracticeDetails"),
  };
};

// 
// MAIN COMPONENT
// 

const IntakeFormPreview = ({ data }) => {
  // State variables
  const [form, setForm] = useState({});
  const [intake, setIntake] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Get the PatientFormId from props (handles different naming conventions)
  const PatientFormId = firstNonEmpty(
    data?.PatientFormId,
    data?.patientFormId,
    data?.patientformId
  );

  // API base URL
  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "https://neuroez4.devforhealth.com/api";

  // 
  // FETCH DATA ON COMPONENT MOUNT
  // 
  useEffect(() => {
    // Don't fetch if no PatientFormId
    if (!PatientFormId) {
      setLoading(false);
      setError("No Patient Form ID provided");
      return;
    }

    // Track if component is still mounted
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        // Step 1: Get auth token from cookies
        const token = localStorage.getItem('neuro_patientportal_auth_token')

        if (!token) {
          throw new Error("Authentication token not found. Please log in.");
        }

        // Set up headers with auth token
        const headers = { Authorization: `Bearer ${token}` };

        // Step 2: Build API URLs
        const formUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${PatientFormId}`;
        const intakeUrl = `${API_BASE_URL}/DoulaForms/getIntakeFormById?PatientFormId=${PatientFormId}`;

        // Step 3: Make BOTH API calls at the same time (faster!)
        const [formResponse, intakeResponse] = await Promise.all([
          axios.get(formUrl, { headers }),
          axios.get(intakeUrl, { headers })
        ]);

        // Step 4: Process form data
        const formRaw = formResponse.data;
        const formData = Array.isArray(formRaw) ? (formRaw[0] || {}) : (formRaw || {});

        // Step 5: Process intake data
        const intakeArray = asArray(intakeResponse.data);
        const firstIntake = intakeArray[0] || {};
        const intakeData = normalizeIntake(firstIntake);

        // Step 6: Update state (only if component is still mounted)
        if (isMounted) {
          setForm(formData);
          setIntake(intakeData);
        }

      } catch (err) {
        // Handle errors
        if (isMounted) {
          console.error("Error fetching data:", err);
          setError(err.message || "Failed to load Intake Form.");
        }
      } finally {
        // Always stop loading
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Run the fetch function
    fetchData();

    // Cleanup function - runs when component unmounts
    return () => {
      isMounted = false;
    };
  }, [API_BASE_URL, PatientFormId]); // Re-run if these values change

  // 
  // RENDER LOADING STATE
  // 
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // 
  // RENDER ERROR STATE
  // 
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // 
  // PREPARE DATA FOR DISPLAY
  // 
  const headerTitle = "Intake Form";
  const dosVal = formatDate(firstNonEmpty(intake.DOS, form.assessmentDate));
  const fileNo = firstNonEmpty(form.accountNo) || "—";
  const clientName = firstNonEmpty(form.patientName, form.PatientNameclient) || "—";
  const dobVal = formatDate(firstNonEmpty(form.dob, form.dateOfBirth));

  // Medical rows - only show rows where condition is true
  const medicalRows = [
    {
      cond: intake.pregnancyAfterALoss,
      label: "Pregnancy after a loss and history to share",
      detail: firstNonEmpty(intake.pregnancyAfterALossDetails),
    },
    {
      cond: intake.aspectsOfYourConception,
      label: "Conception/pregnancy aspects to share",
      detail: firstNonEmpty(intake.aspectsOfYourConceptionDetails),
    },
    {
      cond: intake.previousBirth,
      label: "Previous birth(s) description",
      detail: firstNonEmpty(intake.previousBirthDetails),
    },
    {
      cond: intake.childbirthEducation,
      label: "Childbirth education class taken",
      detail: firstNonEmpty(intake.childbirthEducationDetails),
    },
    {
      cond: intake.physicalActivity,
      label: "Physical activity during pregnancy",
      detail: firstNonEmpty(intake.physicalActivityDetails),
    },
    {
      cond: intake.anyPractitioners,
      label: "Practitioners you're seeing",
      detail: firstNonEmpty(intake.anyPractitionersDetails),
    },
    {
      cond: intake.normallyManagePain,
      label: "How you normally manage pain",
      detail: firstNonEmpty(intake.normallyManagePainDetails),
    },
    {
      cond: intake.PastMedicalHistory,
      label: "Past medical history (physical/mental)",
      detail: firstNonEmpty(intake.PastMedicalHistoryDetails),
    },
    {
      cond: intake.AnyPhysicalLimitations,
      label: "Any physical limitations",
      detail: firstNonEmpty(intake.AnyPhysicalLimitationsDetails),
    },
    {
      cond: intake.pregnancyFromFertilityTreatment,
      label: "Pregnancy from fertility treatment",
      detail: firstNonEmpty(intake.pregnancyFromFertilityTreatmentDetails),
    },
    {
      cond: intake.CurrentMedicalHistory,
      label: "Current medical history for this pregnancy",
      detail: firstNonEmpty(intake.CurrentMedicalHistoryDetails),
    },
    {
      cond: intake.havingIssuesCurrently,
      label: "Bowel/bladder issues currently",
      detail: firstNonEmpty(intake.havingIssuesCurrentlyDetails),
    },
    {
      cond: intake.exerciseBall,
      label: "Exercise ball available",
      detail: firstNonEmpty(intake.exerciseBallDetails),
    },
    {
      cond: intake.historyOfTrauma,
      label: "History of trauma to discuss",
      detail: firstNonEmpty(intake.historyOfTraumaDetails),
    },
    {
      cond: intake.petsInYourHome,
      label: "Pets in your home",
      detail: firstNonEmpty(intake.petsInYourHomeDetails),
    },
    {
      cond: intake.CulturalOrReligiousPractice,
      label: "Cultural/Religious practice(s)",
      detail: firstNonEmpty(intake.CulturalOrReligiousPracticeDetails),
    },
  ].filter((r) => !!r.cond); // Remove rows where condition is false

  // 
  // RENDER THE FORM
  // 
  return (
    <Paper sx={{ p: 0, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 3px 8px rgba(0,0,0,0.08)" }}>
      {/* Header Section */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosVal}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
            color: "#000",
          }}
        >
          {headerTitle}
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient Info Strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #:</strong> {fileNo}
            </Typography>
          </Grid>
          <Grid item xs={4} textAlign="center">
            <Typography variant="body2">
              <strong>Client Name:</strong> {clientName}
            </Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="body2">
              <strong>DOB:</strong> {dobVal}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Main Table Content */}
      <Box
        sx={{
          px: 4,
          pb: 4,
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
            fontSize: "12pt",
            background: "white",
            wordWrap: "break-word",
          }}
        >
          <tbody>
            {/* Personal Information Section */}
            <Section title="Personal Information" />
            <Row label="Preferred Name/Nickname" value={firstNonEmpty(intake.preferredName) || "—"} />
            <Row label="Pronouns" value={firstNonEmpty(intake.pronouns) || "—"} />
            <Row label="Cell Phone Number" value={firstNonEmpty(intake.cellPhone) || "—"} />
            <Row label="Home Address" value={firstNonEmpty(intake.homeAddress) || "—"} />

            {/* Family & Maternal Info Section */}
            <Section title="Family, Home, and Maternal Information" />
            <Row
              label="If not living solo, who lives with you?"
              value={firstNonEmpty(intake.currentlyLivingSolo) || "—"}
            />
            <Row label="Allergies and reactions" value={firstNonEmpty(intake.allergies) || "—"} />
            <Row
              label="Food intolerances/aversions/preferences"
              value={firstNonEmpty(intake.foodIntolerances) || "—"}
            />
            <Row label="Estimated Due Date" value={formatDate(firstNonEmpty(intake.dueDate))} />
            <Row label="Sex of Baby" value={firstNonEmpty(intake.sexOfBaby) || "—"} />
            <Row label="Pregnancy number" value={firstNonEmpty(intake.pregnancyNumber) || "—"} />
            <Row label="Children at home" value={firstNonEmpty(intake.manyChildren) || "—"} />
            <Row label="Names and ages" value={firstNonEmpty(intake.namesAndAges) || "—"} />

            {/* Medical Info Section (only if there are rows to show) */}
            {medicalRows.length > 0 && (
              <>
                <Section title="Medical and Additional Information" />
                {medicalRows.map((r, idx) => (
                  <CheckRow key={idx} label={r.label} detail={r.detail} />
                ))}
              </>
            )}

            {/* Signatures Section */}
            <Section title="Signatures" />
            <Row
              label="Certified Doula Signature"
              value={firstNonEmpty(form.messageCertifiedDoulaSignature, "—")}
            />
            <Row
              label="Supervisor Signature"
              value={firstNonEmpty(form.messageSupervisorSignature, "—")}
            />
            <Row label="RN Signature" value={firstNonEmpty(form.messageRnSignature, "—")} />
            <Row label="NP Signature" value={firstNonEmpty(form.messageNpSignature, "—")} />
            <Row
              label="OBGYN Physician Signature"
              value={firstNonEmpty(form.messageObgynPhysicianSignature, "—")}
            />
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default IntakeFormPreview;